<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { BaseAvatar, BaseIcon } from '@magma-app/magma-lapilli';
import type { HelperRelationState } from '@/composables/useRelationState';
const { t, locale } = useI18n({ useScope: 'global' });
const props = defineProps<{
  state: HelperRelationState;
  prospectFirstname: string;
  prospectInitials: string;
  prospectAvatar?: string | null;
  helperAvatar?: string | null;
  helperInitials: string;
  organizationLogo?: string | null;
  helperValidatedAt?: string | Date | null;
  prospectValidatedAt?: string | Date | null;
  reviewedAt?: string | Date | null;
  reviewedBy?: 'admin' | 'system' | null;
}>();
const failedImages = reactive<Record<string, boolean>>({});
watch(
  () => [props.prospectAvatar, props.helperAvatar, props.organizationLogo],
  () => {
    Object.keys(failedImages).forEach((key) => delete failedImages[key]);
  }
);
const dateLabel = (date?: string | Date | null) =>
  date
    ? new Date(date).toLocaleDateString(locale.value, {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    : '';
const parties = computed(() => {
  const approved = ['accepted', 'exchangeValidated'].includes(props.state);
  const closed = props.state === 'closedUnreviewed';
  const teamKey = approved
    ? 'approved'
    : props.state === 'rejected'
      ? 'notApproved'
      : closed
        ? props.reviewedBy === 'system'
          ? 'automatic'
          : 'closed'
        : props.state === 'reported'
          ? 'notApplicable'
          : props.state === 'waitingTeam'
            ? 'pending'
            : 'upcoming';
  return [
    {
      key: 'prospect',
      name: props.prospectFirstname,
      avatar: props.prospectAvatar,
      initials: props.prospectInitials,
      done: !!props.prospectValidatedAt,
      label: t(
        `helperRelation.parties.${
          props.prospectValidatedAt ? 'confirmed' : 'notConfirmed'
        }`
      ),
      date: props.prospectValidatedAt
    },
    {
      key: 'helper',
      name: t('helperRelation.parties.you'),
      avatar: props.helperAvatar,
      initials: props.helperInitials,
      done: !!props.helperValidatedAt,
      label: t(
        `helperRelation.parties.${
          props.helperValidatedAt ? 'confirmed' : 'notConfirmed'
        }`
      ),
      date: props.helperValidatedAt
    },
    {
      key: 'team',
      name: t('helperRelation.parties.team'),
      avatar: props.organizationLogo || '/img/logo_magma.png',
      initials: '',
      done: approved,
      label: t(`helperRelation.parties.${teamKey}`),
      date: props.reviewedAt
    }
  ];
});
</script>

<template>
  <div
    class="grid grid-cols-2 gap-x-4 gap-y-4 py-1 sm:flex sm:flex-wrap sm:gap-x-12"
    data-cy="relation-parties"
  >
    <div
      v-for="party in parties"
      :key="party.key"
      class="flex min-w-0 items-center gap-3"
      @error.capture="failedImages[party.key] = true"
    >
      <div class="h-10 w-10 shrink-0 sm:h-12 sm:w-12">
        <BaseAvatar
          class="h-full [&>div]:text-mgm-txt-sm"
          :src="
            failedImages[party.key]
              ? party.key === 'team'
                ? '/img/logo_magma.png'
                : null
              : party.avatar
          "
          :fallback="party.initials"
          :alt="party.name"
          size="full"
          :shape="party.key === 'team' ? 'rounded' : 'circle'"
        />
      </div>
      <div class="min-w-0 text-mgm-txt-xs">
        <p class="max-w-40 break-words font-medium text-foreground-emphasis">
          {{ party.name }}
        </p>
        <p
          class="mt-1 flex items-center gap-1.5 text-foreground-default"
          :title="dateLabel(party.date)"
        >
          <BaseIcon
            :icon="party.done ? 'check-circle' : 'clock'"
            :size="16"
            class="shrink-0"
            :color="
              party.done
                ? 'var(--color-foreground-success-default)'
                : 'var(--color-foreground-subtle)'
            "
          />
          <span>{{ party.label }}</span>
          <span v-if="party.date" class="sr-only">{{
            dateLabel(party.date)
          }}</span>
        </p>
      </div>
    </div>
  </div>
</template>
